import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import {
  vendorStep1Schema,
  vendorStep2Schema,
} from "../../../schemas/vendorOnboardingSchema";
import {
  useAcceptVendorAgreement,
  useBlobToken,
  useGetVendorAcceptAgreement,
  useGetVendorActiveAgreement,
  useVendorSignUp,
} from "../../../custom-hooks/queries";
import { getCleanedUrl, uploadFile } from "../../../utils/HelperMethod";
import { useAuthStore } from "../../../store/useAuthStore";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiLoader } from "react-icons/fi";
import { toast } from "react-toastify";

const VendorOnboarding = () => {
  const [step, setStep] = useState(1);
  const [step1Data, setStep1Data] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const { setAuth } = useAuthStore();

  const { data: activeVendorAgreement } = useGetVendorActiveAgreement();

  const { mutate: acceptVendorAgreement } = useAcceptVendorAgreement();

  // Step 1 Form
  const step1Form = useForm({
    resolver: yupResolver(vendorStep1Schema),
  });

  // Step 2 Form
  const step2Form = useForm({
    resolver: yupResolver(vendorStep2Schema),
    defaultValues: {
      agreeToTerms: false,
    },
  });

  const { mutate: blobToken } = useBlobToken();
  const { mutate: vendorSignUp } = useVendorSignUp();
  const navigate = useNavigate();

  const onStep1Submit = (data) => {
    setStep1Data(data);
    setStep(2);
  };

  const [loading, setLoading] = useState(false);

  const onStep2Submit = async (data) => {
    setLoading(true);

    const finalData = { ...step1Data, ...data };
    const logoUrl = await uploadFile(step1Data?.logo, "vendorLogo");
    const cleanedLogo = getCleanedUrl(logoUrl);
    const govIdUrl = await uploadFile(data?.governmentId, "govId");
    const insUrl = await uploadFile(data?.liabilityInsurance, "insUrl");

    const payload = {
      contact_name: finalData.contactName || "",
      email: finalData.email || "",
      password: finalData.password || "",
      phone: finalData.phone || "",
      business_name: finalData.businessName || "",
      business_description: finalData.businessDescription || "",
      business_address: finalData.businessAddress || "",
      business_license_number: finalData.businessLicenseNumber || "",
      tax_id: finalData.einTaxId || "",
      logo_url: cleanedLogo,
      government_id_url: govIdUrl,
      liability_insurance_url: insUrl,
      accept_terms_and_policy: finalData.agreeToTerms || false,
    };

    vendorSignUp(payload, {
      onSuccess: (res) => {
        setLoading(false);
        acceptVendorAgreement(activeVendorAgreement?.agreementId);

        // Response: { vendorId, role, email, businessName, verificationStatus, message }
        const vendor = res?.data || res || {};

        setAuth({
          token: res?.data?.token || res?.token,
          role: vendor?.role || "Vendor",
          user: {
            id: vendor?.vendorId,
            name: finalData.contactName || vendor?.businessName || "",
            firstName: finalData.contactName?.split(" ")[0] || "",
            lastName:
              finalData.contactName?.split(" ").slice(1).join(" ") || "",
            email: vendor?.email || finalData.email,
            businessName: vendor?.businessName || finalData.businessName,
            avatar: cleanedLogo || null,
            verificationStatus: vendor?.verificationStatus,
          },
        });

        toast.success(
          "Account created successfully! Please verify your email.",
        );
        navigate("/otp", { state: { flow: "signup" }, replace: true });
      },
      onError: (error) => {
        setLoading(false);
        const errorMessage =
          error?.response?.data?.error_message ||
          error?.error_message ||
          "Failed to create account. Please try again.";
        toast.error(errorMessage);
      },
    });
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleFileChange = (fieldName, form, setValue) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setValue(fieldName, file, { shouldValidate: true });
    }
  };

  // STEP 1 COMPONENT
  const Step1 = () => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
      watch,
    } = step1Form;

    const logoFile = watch("logo");
    const [showPassword, setShowPassword] = useState(false);

    return (
      <form onSubmit={handleSubmit(onStep1Submit)} className="space-y-5">
        {/* Upload Logo */}
        <div>
          <label
            htmlFor="logo"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Upload Logo
          </label>
          <div className="relative">
            <input
              id="logo"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange("logo", step1Form, setValue)}
              className="hidden"
            />
            <label
              htmlFor="logo"
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                errors.logo ? "border-red-500 bg-red-50" : "border-gray-300"
              }`}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg
                  className="w-10 h-10 mb-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Drop your logo here</span> or
                  upload
                </p>
                <p className="text-xs text-gray-500">JPG, PNG up to 5MB</p>

                {logoFile && (
                  <p className="mt-2 text-xs text-[#00C2C7]">{logoFile.name}</p>
                )}
              </div>
            </label>
          </div>
          {errors.logo && (
            <p className="mt-1 text-sm text-red-600">{errors.logo.message}</p>
          )}
        </div>

        {/* Business Name */}
        <div>
          <label
            htmlFor="businessName"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Business Name
          </label>
          <input
            id="businessName"
            type="text"
            {...register("businessName")}
            className={`w-full px-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
              errors.businessName ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Ex. Global Adventures"
          />
          {errors.businessName && (
            <p className="mt-1 text-sm text-red-600">
              {errors.businessName.message}
            </p>
          )}
        </div>

        {/* Contact Name */}
        <div>
          <label
            htmlFor="contactName"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Contact Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <input
              id="contactName"
              type="text"
              {...register("contactName")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.contactName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Your Full Name"
            />
          </div>
          {errors.contactName && (
            <p className="mt-1 text-sm text-red-600">
              {errors.contactName.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Email
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Your Email Address"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Password
          </label>

          <div className="relative">
            {/* Lock Icon */}
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>

            {/* Input */}
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              {...register("password")}
              className={`w-full pl-10 pr-10 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.password ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Create a Strong Password"
            />

            {/* Eye Toggle */}
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {errors.password && (
            <p className="mt-1 text-sm text-red-600">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Business Description */}
        <div>
          <label
            htmlFor="businessDescription"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Business Description
          </label>
          <textarea
            id="businessDescription"
            {...register("businessDescription")}
            rows={4}
            className={`w-full px-4 py-2.5 border rounded-lg resize-none placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
              errors.businessDescription ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Tell us about your awesome experiences"
          />
          {errors.businessDescription && (
            <p className="mt-1 text-sm text-red-600">
              {errors.businessDescription.message}
            </p>
          )}
        </div>

        {/* Next Button */}
        <button
          type="submit"
          className="w-full bg-[#00C2C7] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          Next
        </button>

        {/* Sign In Link */}
        <p className="text-center text-sm text-gray-600">
          Already have an account?{" "}
          <Link to="/signin" className="font-medium text-[#1FADC6] underline">
            Sign in
          </Link>
        </p>
      </form>
    );
  };

  // STEP 2 COMPONENT
  const Step2 = () => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
      watch,
    } = step2Form;

    const governmentIdFile = watch("governmentId");
    const liabilityInsuranceFile = watch("liabilityInsurance");
    const agreeToTerms = watch("agreeToTerms");

    return (
      <form onSubmit={handleSubmit(onStep2Submit)} className="space-y-5">
        {/* Legal Business Name */}

        {/* EIN/Tax ID */}
        <div>
          <label
            htmlFor="einTaxId"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            EIN/Tax ID
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <input
              id="einTaxId"
              type="text"
              {...register("einTaxId")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.einTaxId ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Tax Identification"
            />
          </div>
          {errors.einTaxId && (
            <p className="mt-1 text-sm text-red-600">
              {errors.einTaxId.message}
            </p>
          )}
        </div>

        {/* Business License Number */}
        <div>
          <label
            htmlFor="businessLicenseNumber"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Business License Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <input
              id="businessLicenseNumber"
              type="text"
              {...register("businessLicenseNumber")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.businessLicenseNumber
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Enter License Number"
            />
          </div>
          {errors.businessLicenseNumber && (
            <p className="mt-1 text-sm text-red-600">
              {errors.businessLicenseNumber.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Phone
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
            <input
              id="phone"
              type="tel"
              {...register("phone")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.phone ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Phone Number"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        {/* Business Address */}
        <div>
          <label
            htmlFor="businessAddress"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Business Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <input
              id="businessAddress"
              type="text"
              {...register("businessAddress")}
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#1FADC6] ${
                errors.businessAddress ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Business Address"
            />
          </div>
          {errors.businessAddress && (
            <p className="mt-1 text-sm text-red-600">
              {errors.businessAddress.message}
            </p>
          )}
        </div>

        {/* Government ID */}
        <div>
          <label
            htmlFor="governmentId"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Government-Issued ID
          </label>
          <div className="relative">
            <input
              id="governmentId"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange("governmentId", step2Form, setValue)}
              className="hidden"
            />
            <label
              htmlFor="governmentId"
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                errors.governmentId
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300"
              }`}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg
                  className="w-10 h-10 mb-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Drop Your ID here</span> or
                  upload
                </p>
                <p className="text-xs text-gray-500">JPG, PNG up to 5MB</p>

                {governmentIdFile && (
                  <p className="mt-2 text-xs text-[#00C2C7]">
                    {governmentIdFile.name}
                  </p>
                )}
              </div>
            </label>
          </div>
          {errors.governmentId && (
            <p className="mt-1 text-sm text-red-600">
              {errors.governmentId.message}
            </p>
          )}
        </div>

        {/* Liability Insurance */}
        <div>
          <label
            htmlFor="liabilityInsurance"
            className="block text-sm font-medium text-gray-800 mb-1"
          >
            Proof of Liability Insurance
          </label>
          <div className="relative">
            <input
              id="liabilityInsurance"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileChange(
                "liabilityInsurance",
                step2Form,
                setValue,
              )}
              className="hidden"
            />
            <label
              htmlFor="liabilityInsurance"
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                errors.liabilityInsurance
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300"
              }`}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg
                  className="w-10 h-10 mb-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Drop Your ID here</span> or
                  upload
                </p>
                <p className="text-xs text-gray-500">JPG, PNG up to 5MB</p>

                {liabilityInsuranceFile && (
                  <p className="mt-2 text-xs text-[#00C2C7]">
                    {liabilityInsuranceFile.name}
                  </p>
                )}
              </div>
            </label>
          </div>
          {errors.liabilityInsurance && (
            <p className="mt-1 text-sm text-red-600">
              {errors.liabilityInsurance.message}
            </p>
          )}
        </div>

        {/* Terms checkbox */}
        <div className="flex items-center">
          <input
            id="agreeToTerms"
            type="checkbox"
            checked={agreeToTerms}
            onChange={(e) =>
              setValue("agreeToTerms", e.target.checked, {
                shouldValidate: true,
              })
            }
            className="h-4 w-4 text-[#00C2C7] border-gray-300 rounded focus:ring-[#1FADC6] cursor-pointer"
          />
          <label
            htmlFor="agreeToTerms"
            className="ml-2 mt-1 text-[12px] text-gray-800 font-medium leading-none"
          >
            I agree to the{" "}
          </label>
          <a
            href={activeVendorAgreement?.documentUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-[#1FADC6] hover:text-[#00C2C7] text-[12px] font-medium ml-1"
            onClick={(e) => e.stopPropagation()}
          >
            Toucan Vendor Agreement
          </a>
        </div>

        {errors.agreeToTerms && (
          <p className="text-sm text-red-600 -mt-3">
            {errors.agreeToTerms.message}
          </p>
        )}

        {/* Create Account */}
        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 bg-[#16BBD8] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          {loading && <FiLoader className="animate-spin" />}
          <span>Create Account</span>
        </button>
      </form>
    );
  };

  // ============================
  // MAIN UI CONTAINER
  // ============================

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-[650px] flex flex-col sm:flex-row items-center sm:items-start gap-4">
        {/* Back Button aligned with card */}
        {step === 2 && (
          <button
            onClick={handleBack}
            className="p-2 rounded-full bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition-colors h-10 mt-2 self-start sm:self-auto"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* CARD */}
        <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-6 sm:p-10 bg-white mx-auto">
          {/* Heading */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
            Become A vendor
          </h1>

          <p className="text-sm sm:text-base text-gray-600 text-center mb-4">
            Join our community and showcase your experience
          </p>

          {/* Decorative Rectangle */}
          <div className="flex justify-center mb-8">
            <div className="w-[165px] h-[5px] bg-[#00C2C7] rounded"></div>
          </div>

          {/* Steps */}
          {step === 1 ? <Step1 /> : <Step2 />}
        </div>
      </div>
    </div>
  );
};

export default VendorOnboarding;
